import {
  AutoPlayObjectType,
  PromoType,
  RowDataItem,
  Provider,
  KitchenSinkFixtureOrderItem,
  KitchenSinkFixturePage,
} from 'app-types';
import {
  KitchenSinkMenuItem,
  KitchenSinkTag,
  KitchenSinkRowItem,
} from 'store-types';
import { isTrendingRow, TRENDING_ROW_MAX_ITEMS } from 'support/rowHelper';
import log from 'common/support/Log';
import { rewriteAirspaceCdnUrls } from 'support/airspaceCdnProxy';

const DAI_PROXY_ENABLED = import.meta.env.VITE_GOOGLE_DAI_PROXY === '1';
const AIRSPACE_CDN_PROXY_ENABLED = import.meta.env.VITE_AIRSPACE_CDN_PROXY === '1';
const DAI_ORIGIN = import.meta.env.VITE_GOOGLE_DAI_URL || 'https://dai.google.com';

const swapVideoProxyUrl = (url: string | null | undefined): string | null | undefined => {
  if (!url) return url;
  let result = url;
  if (DAI_PROXY_ENABLED && result.startsWith(DAI_ORIGIN)) {
    result = result.replace(DAI_ORIGIN, '/dai-proxy');
  }
  if (AIRSPACE_CDN_PROXY_ENABLED) {
    result = rewriteAirspaceCdnUrls(result);
  }
  return result;
};

type ParseVideoInput = AutoPlayObjectType | RowDataItem | Record<string, unknown>;

interface TagItem {
  tag?: KitchenSinkTag;
  id?: number;
  name?: string;
  displayName?: string;
  description?: string;
  leagues?: unknown[];
}

interface TagRows {
  sports?: TagItem[];
  leagues?: TagItem[];
  markets?: TagItem[];
  shows?: TagItem[];
}

// MenuItem extends KitchenSinkMenuItem but with looser action type for parsing
interface MenuItem extends Omit<KitchenSinkMenuItem, 'action'> {
  action: string;
}

interface ProviderConfig {
  secondScreenUrl: string;
  authUrl?: string;
  authPublicKey?: string;
  authEmail?: string;
  clientId?: string;
  clientSecret?: string;
  logoUrlsmall?: string;
  logoUrlSmall?: string;
  logoUrlmedium?: string;
  logoUrlMedium?: string;
  logoUrllarge?: string;
  logoUrlLarge?: string;
  displayName?: string;
  mvpdId?: string;
  premiumUpgradeMessage?: string;
}

interface ProvidersConfig {
  adobe: ProviderConfig;
  paramountplus: ProviderConfig;
}

interface NetworkConfig {
  resource: string;
  name: string;
  logoUrlSmall: string;
  logoUrlMedium: string;
  logoUrlLarge: string;
  logoUrlFixedSizeSmall: string;
  logoUrlFixedSizeMedium: string;
  logoUrlFixedSizeLarge: string;
}

interface SettingsConfig {
  legal?: {
    label: string;
    enabled: boolean;
    link: string;
  };
}

interface GlobalSettingsConfig {
  max429Length: number;
}

interface PlaybackConfig {
  [key: string]: {
    playbackHost?: string;
    playbackKey?: string;
    tokenUrl?: string;
    widevine?: { license: string };
    playready?: { license: string };
    fairplay?: { license: string; cert: string };
    playbackMatrix?: {
      adobe?: string;
      both?: string;
      none?: string;
      paramountplus?: string;
    };
  };
}

interface HeroConfig {
  properties: Record<string, unknown>;
  data: ParseVideoInput[];
}

interface PregameItem {
  identifiers: { trackingId: string };
  viacomContentId: string;
  network: string;
  title: string;
  useDai: boolean;
  dai?: {
    daiIu: string;
    daiAssetKey: string;
    daiDashAssetKey?: string;
    daiApiKey: string;
    daiOt: string;
    daiOv: string;
    daiApto: number;
  };
  thumbnail: { baseImage16X9: string; baseImage16X5: string } | null;
  playback: string;
  sources?: {
    dash?: { url?: string; daiAssetKey?: string };
    hls?: { url?: string; daiAssetKey?: string };
  };
}

let config: Record<string, unknown> = {};

const dropOnError = <R>(id: unknown, parse: () => R): R | null => {
  try {
    return parse();
  } catch (err) {
    log.warn(`Dropping malformed kitchen sink item ${id}: ${log.describeError(err)}`);
    return null;
  }
};

export const parseKitchenSink = (response: { page: KitchenSinkFixturePage }) => {
  const {
    version,
    menuId,
    config: ksConfig,
    order,
    rows,
    autoplay,
    autoplayObject,
    providers,
    networks,
    settings,
    globalSettings,
    playback,
    hero,
    menu,
    tag_rows: tagRows,
  } = response.page;

  config = parseConfig(ksConfig);

  return {
    version,
    menuId,
    menu: parseMenu(menu as MenuItem[]),
    order: parseOrder(order, rows as Record<string, RowDataItem[]>),
    config: parseConfig(ksConfig),
    rows: parseRows(order, rows as Record<string, RowDataItem[]>),
    autoplay,
    autoplayObject: parseVideo(autoplayObject as ParseVideoInput),
    providers: parseProviders(providers as unknown as ProvidersConfig),
    networks: parseNetworks(networks as Record<string, NetworkConfig>),
    settings: parseSettings(settings as SettingsConfig),
    globalSettings: parseGlobalSettings(globalSettings as unknown as GlobalSettingsConfig),
    playback: parsePlayback(playback as PlaybackConfig),
    hero: parseHero(hero as unknown as HeroConfig),
    sports: parseSports(tagRows as TagRows),
    leagues: parseLeagues(tagRows as TagRows),
    allsports: parseAllSports(tagRows as TagRows),
    localMarkets: parseLocalMarkets(tagRows as TagRows),
    shows: parseShows(tagRows as TagRows),
  };
};

export const parseKitchenSinkRows = (response: { page: { rows: Record<string, RowDataItem[]> } }) => {
  const { rows } = response.page;
  return Object.keys(rows).reduce((accumulator, rowId) => {
    const parsed = parseRowData(rows[rowId]);
    if (parsed && parsed.length) {
      (accumulator as Record<string, unknown>)[rowId] = parsed;
    }
    return accumulator;
  }, {});
};

export const parseKitchenSinkHero = (response: { page: { hero?: { properties: unknown; data: ParseVideoInput[] } } }) => {
  const { hero } = response.page;

  if (!hero) return {};

  const { properties, data } = hero;
  const heroData = data.map(item => parseVideo(item)).filter(item => item !== null);

  return { properties, data: heroData };
};

const parseConfigModule = (module: { params: Array<{ name: string; value: string }>; category: string; enabled: boolean }) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: Record<string, any> = {};

  for (const { name, value } of module.params) {
    if (Object.prototype.hasOwnProperty.call(params, name)) {
      // Convert to array or push to existing array
      if (Array.isArray(params[name])) {
        params[name].push(value);
      } else {
        params[name] = [params[name], value];
      }
    } else {
      params[name] = value;
    }
  }

  return {
    params,
    category: module.category,
    enabled: module.enabled,
  };
};

const parseConfig = (ksConfig: KitchenSinkFixturePage['config']) => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    modules: ksConfig?.modules?.reduce((accumulator: Record<string, any>, module) => {
      accumulator[module.name] = parseConfigModule(module);
      return accumulator;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    }, {} as Record<string, any>),
    classes: ksConfig?.classes,
  };
};

export const parseVideo = (item: ParseVideoInput) => dropOnError(item?.id, () => {
  const { id, order, featured, dmaStation, video } = item as AutoPlayObjectType;
  const {
    about = {} as AutoPlayObjectType['video']['about'],
    ads = {} as AutoPlayObjectType['video']['ads'],
    authenticationDetails = {} as AutoPlayObjectType['video']['authenticationDetails'],
    authentication = [] as AutoPlayObjectType['video']['authentication'],
    event = {} as AutoPlayObjectType['video']['event'],
    flags = {} as AutoPlayObjectType['video']['flags'],
    identifiers = {} as AutoPlayObjectType['video']['identifiers'],
    network = null,
    playback,
    properties = {} as AutoPlayObjectType['video']['properties'],
    schedule = {} as AutoPlayObjectType['video']['schedule'],
    sources: rawSources,
  } = video;
  if (typeof playback !== 'string' || !VALID_PLAYBACK_TYPES.includes(playback as PlaybackType)) {
    return null;
  }

  const sources = rawSources ?? ({} as AutoPlayObjectType['video']['sources']);
  return {
    id,
    dmaStation,
    trackingId: identifiers.trackingId,
    viacomContentId: identifiers.viacomContentId,
    type: 'VIDEO',
    order,
    isFeatured: featured,
    network,
    about: {
      title: about.title,
      shortTitle: about.shortTitle,
      segmentTitle: about.segmentTitle,
      duration: about.duration,
      description: about.description,
      publishedOn: about.publishedOn,
      prefix: about.prefix,
    },
    ads: {
      isDai: flags.useDai,
      daiIu: ads.dai?.daiIu,
      daiAssetKey: sources.hls?.daiAssetKey ?? ads.dai?.daiAssetKey,
      daiDashAssetKey: sources.dash?.daiAssetKey ?? ads.dai?.daiDashAssetKey,
      daiApiKey: ads.dai?.daiApiKey,
      daiOt: ads.dai?.daiOt,
      daiOv: ads.dai?.daiOv,
      daiApto: ads.dai?.daiApto,
      daiCsid: ads.dai?.daiCsid,
    },
    authenticationDetails,
    authTypes: authentication,
    event: {
      status: event.status,
      preEvent: event.preEvent,
      postEvent: event.postEvent,
      pregame: parsePregame(event.pregame as unknown as PregameItem | undefined, authentication),
    },
    images: about.images && {
      baseImage16X9: about.images.baseImage16X9,
      baseImage16X5: about.images.baseImage16X5,
    },
    playback: {
      isSyncbak: playback === PlaybackTypes.SYNCBAK,
      url: swapVideoProxyUrl(
        playback === PlaybackTypes.FULL_DRM ? sources.dash?.url : sources.hls?.url,
      ),
      type: playback,
    },
    properties: {
      type: properties.type,
      gameId: properties.gameId,
      league: properties.league,
    },
    schedule: {
      preStartDate: schedule.preStartDate,
      preEndDate: schedule.preEndDate,
      videoStartDate: schedule.videoStartDate,
      videoEndDate: schedule.videoEndDate,
      postStartDate: schedule.postStartDate,
      postEndDate: schedule.postEndDate,
      apiStartDate: schedule.apiStartDate,
      apiEndDate: schedule.apiEndDate,
    },
  };
});

const parsePregame = (item: PregameItem | undefined, authentication: Provider[]) => {
  if (item == null) return null;
  return {
    trackingId: item.identifiers.trackingId,
    viacomContentId: item.viacomContentId,
    network: item.network,
    about: {
      title: item.title,
    },
    ads: {
      isDai: item.useDai,
      daiIu: item.dai?.daiIu,
      daiAssetKey: item.sources?.hls?.daiAssetKey ?? item.dai?.daiAssetKey,
      daiDashAssetKey: item.sources?.dash?.daiAssetKey ?? item.dai?.daiDashAssetKey,
      daiApiKey: item.dai?.daiApiKey,
      daiOt: item.dai?.daiOt,
      daiOv: item.dai?.daiOv,
      daiApto: item.dai?.daiApto,
    },
    images: item.thumbnail,
    playback: {
      isSyncbak: item.playback === PlaybackTypes.SYNCBAK,
      url: swapVideoProxyUrl(
        item.playback === PlaybackTypes.FULL_DRM ? item.sources?.dash?.url : item.sources?.hls?.url,
      ),
      type: item.playback,
    },
    authTypes: authentication,
  };
};

const parsePromo = (id: string, promo: PromoType) => {
  return {
    id,
    type: 'PROMO',
    title: promo.title,
    images: promo.images && {
      baseImage16X9: promo.images.baseImage16X9,
      baseImage16X5: promo.images.baseImage16X5,
    },
    tagId: promo.tag?.id,
    tagKey: promo.tag?.key,
    tagType: promo.tag?.type?.name,
  };
};

export const parseRowData = (rowData: RowDataItem[] | null | undefined = null) => {
  const modules = config?.modules as Record<string, { params?: Record<string, string> }> | undefined;
  const auth = modules?.Auth?.params;

  if (!Array.isArray(rowData)) {
    return null;
  }

  return rowData
    .map(item =>
      dropOnError(item?.id, () => {
        if (item.promo) {
          return parsePromo(item.id, item.promo);
        }

        if (item.video) {
          const { authentication } = item.video;
          if (auth?.shortCode_PPlus === '0' || auth?.shortCode_MVPD === '0') {
            if (
              authentication.includes('paramountplus') ||
              authentication.includes('adobe')
            ) {
              return null;
            }
          }
          return parseVideo(item);
        }

        return null;
      }),
    )
    .filter(item => item !== null);
};

const parseRows = (order: KitchenSinkFixtureOrderItem[], rows: Record<string, RowDataItem[]>) => {
  const getLayoutType = (layout: string) => {
    switch (layout) {
      case 'oneacross':
        return 1;
      case 'twoacross':
        return 2;
      case 'threeacross':
      case 'threeAcrossHighlight':
      case 'threeAcrossTopTrending':
        return 3;
      case 'fouracross':
      case 'fourAcrossHighlight':
      case 'fourAcrossTopTrending':
        return 4;
      case 'fiveacross':
      case 'fiveAcrossTopTrending':
      default:
        return 5;
    }
  };

  return order
    .filter(({ id }) => rows[id])
    .map(({ id, type, slug, title: rowTitle, description, url, layout }) => {
      const row = rows[id];
      const parsed = parseRowData(row);

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return null;
      }

      const data = isTrendingRow({ layout } as KitchenSinkRowItem)
        ? parsed.slice(0, TRENDING_ROW_MAX_ITEMS)
        : parsed;

      return {
        data,
        id,
        type,
        slug,
        title: rowTitle,
        description,
        url,
        layout,
        layoutType: getLayoutType(layout),
      };
    })
    .filter((item) => item !== null);
};

const parseProviders = (providers: ProvidersConfig) => {
  const { adobe, paramountplus } = providers;

  const adobeResult = {
    secondScreenUrl: adobe.secondScreenUrl,
    authUrl: adobe.authUrl,
    key: adobe.authPublicKey,
    email: adobe.authEmail,
  };

  const paramountplusResult = {
    secondScreenUrl: paramountplus.secondScreenUrl,
    clientId: paramountplus.clientId,
    clientSecret: paramountplus.clientSecret,
    // Checking for different logo keys because response returns inconsistent casing
    logoSmallUrl: paramountplus.logoUrlsmall || paramountplus.logoUrlSmall,
    logoMediumUrl: paramountplus.logoUrlmedium || paramountplus.logoUrlMedium,
    logoLargeUrl: paramountplus.logoUrllarge || paramountplus.logoUrlLarge,
    email: paramountplus.authEmail,
    displayName: paramountplus.displayName,
    syncbakMvpdId: paramountplus.mvpdId,
    premiumUpgradeMessage: paramountplus.premiumUpgradeMessage,
  };

  return {
    adobe: adobeResult,
    paramountplus: paramountplusResult,
  };
};

const parseNetworks = (networks: Record<string, NetworkConfig>) => {
  const result: Record<string, unknown> = {};
  Object.keys(networks).forEach(networkId => {
    const data = networks[networkId];
    result[networkId] = {
      resourceId: data.resource,
      name: data.name,
      logoSmallUrl: data.logoUrlSmall,
      logoMediumUrl: data.logoUrlMedium,
      logoLargeUrl: data.logoUrlLarge,
      logoFixedSmallUrl: data.logoUrlFixedSizeSmall,
      logoFixedMediumUrl: data.logoUrlFixedSizeMedium,
      logoFixedLargeUrl: data.logoUrlFixedSizeLarge,
    };
  });

  return result;
};

const parseSettings = (settings: SettingsConfig) => {
  try {
    return {
      legal: {
        label: settings.legal?.label,
        enabled: settings.legal?.enabled,
        link: settings.legal?.link,
      },
    };
  } catch {
    return {};
  }
};

const parseGlobalSettings = (settings: GlobalSettingsConfig) => {
  return {
    max429RetryMs: settings.max429Length * 1000,
  };
};

const parsePlayback = (playback: PlaybackConfig) => {
  const SYNCBAK_V3 = 'syncbakV3';
  return {
    [SYNCBAK_V3]: {
      host: playback[SYNCBAK_V3].playbackHost,
      key: playback[SYNCBAK_V3].playbackKey,
    },
    [PlaybackTypes.HLS_ENCRYPTION]: {
      tokenUrl: playback[PlaybackTypes.HLS_ENCRYPTION].tokenUrl,
    },
    [PlaybackTypes.FULL_DRM]: {
      tokenUrl: playback[PlaybackTypes.FULL_DRM].tokenUrl,
      widevine: playback[PlaybackTypes.FULL_DRM].widevine?.license,
      playready: playback[PlaybackTypes.FULL_DRM].playready?.license,
      fairplay: {
        licenseUrl: playback[PlaybackTypes.FULL_DRM].fairplay?.license,
        appCertUrl: playback[PlaybackTypes.FULL_DRM].fairplay?.cert,
      },
    },
    [PlaybackTypes.STATION_OPT_IN]: {
      tokenUrl: playback[PlaybackTypes.STATION_OPT_IN]?.tokenUrl,
      playbackMatrix: {
        adobe: playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.adobe,
        both: playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.both,
        none: playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.none,
        paramountplus:
          playback[PlaybackTypes.STATION_OPT_IN]?.playbackMatrix?.paramountplus,
      },
    },
  };
};

const parseHero = (hero: HeroConfig) => {
  const { properties, data } = hero;
  const heroData = data.map(item => parseVideo(item)).filter(item => item !== null);

  return { properties, data: heroData };
};

const parseSports = (tagRows: TagRows | undefined) => {
  const { sports } = tagRows || {};
  if (!sports) {
    return sports;
  }
  return sports.filter((item) => item?.tag?.isActive);
};

const parseAllSports = (tagRows: TagRows | undefined) => {
  const { sports } = tagRows || {};

  if (!sports) {
    return sports;
  }
  return sports
    .map((row) => {
      const parsed = row?.leagues;

      if (!Array.isArray(parsed) || parsed.length === 0) {
        return null;
      }

      return {
        data: parsed,
        id: `${(row.name ?? '').toLowerCase()}-${row.id}`,
        type: 'sportsAndLeagues',
        slug: row.tag?.key,
        title: row.displayName,
        description: row.description,
        url: 'www.cbsnews.com',
        layoutType: 'sportsAndLeagues',
        isActive: row.tag?.isActive,
      };
    })
    .filter((item) => item !== null && item.isActive);
};

const parseLeagues = (tagRows: TagRows | undefined) => {
  const { leagues } = tagRows || {};
  if (!leagues) {
    return leagues;
  }
  return leagues.filter((item) => item?.tag?.isActive);
};

const parseLocalMarkets = (tagRows: TagRows | undefined) => {
  const { markets } = tagRows || {};
  if (!markets) {
    return markets;
  }
  return markets.filter((item) => item?.tag?.isActive);
};

const parseShows = (tagRows: TagRows | undefined) => {
  const { shows } = tagRows || {};
  if (!shows) {
    return shows;
  }
  return shows.filter((item) => item?.tag?.isActive);
};

export const PlaybackTypes = {
  SYNCBAK: 'syncbak',
  FULL_DRM: 'fullDRM',
  OPEN_HLS: 'openHLS',
  HLS_ENCRYPTION: 'hls_encryption',
  STATION_OPT_IN: 'stationOptIn',
  DMA_DAI_OPT_IN: 'dmaDaiOptIn',
} as const;

export type PlaybackType = (typeof PlaybackTypes)[keyof typeof PlaybackTypes];

export const VALID_PLAYBACK_TYPES: readonly PlaybackType[] = Object.values(PlaybackTypes);

const parseOrder = (order: KitchenSinkFixtureOrderItem[], rows: Record<string, RowDataItem[]>) => {
  return order.filter(
    ({ id, type }) =>
      rows[id] || type === 'sportsAndLeagues' || type === 'tagRow',
  );
};

const parseMenu = (menu: MenuItem[]) => {
  const maxTags = 6;
  let tagCount = 0;

  return menu.filter((item) => {
    if (item.action !== 'tag' || tagCount < maxTags) {
      if (item.action === 'tag') tagCount += 1;
      return true;
    }
    return false;
  });
};
